import { createReducer } from "reduxsauce"
import { findIndex, get } from "lodash"
import { call, getContext, put, select, takeLatest } from "redux-saga/effects"
import actionCreator from "helpers/actionCreator"
import { sendQuery } from "api/graphql"
import { getUserPreference } from "helpers/userPreferences"
import { Creators as notificationCreators } from "modules/notifications/model"
import { Creators as modelCreators, Types as LoaderTypes } from "model/data"
import { Creators as PreferencesCreators } from "model/preferences"
import { getModels } from "./selectors"

export const { Types, Creators } = actionCreator("ompnt/modules/researchTopicRoot", {
  updateTopic: {
    props: ["topicId", "update"],
    track: true,
    trackType: "resources-update-complete",
    trackPayload: () => ({ resourceType: "topic" }),
  },
  deleteTopic: {
    props: ["id"],
    track: true,
    trackType: "resources-delete-complete",
    trackPayload: () => ({ resourceType: "topic" }),
  },
  setModel: {
    props: ["modelId"],
    updatePreference: {
      getItems: (state, { modelId }) => [{
        type: "researchTopicLastModelUsed",
        value: modelId,
      }],
    },
  },
  researchTopicPreferencesSuccess: ["modelId"],
  setSelectedDate: ["selectedDate"],
  resetSelectedDate: [],
  setPeriod: {
    props: ["period"],
    track: true,
    trackType: "research-topic-date-period-updated",
    trackPayload: ({ period }) => ({ period }),
  },
  loadSnapshot: ["payload"],
  setSnapshot: ["snapshot"],
})

const DEFAULT_STATE = {
  period: "1Y",
  selectedDate: null,
  selectedModel: null,
  snapshot: null,
}

function handleResearchTopicPreferencesSuccess(state, { modelId }) {
  return { ...state, selectedModel: modelId }
}

function handleSetModel(state, { modelId }) {
  return { ...state, selectedModel: modelId }
}

function handleSetPeriod(state, { period }) {
  return { ...state, period }
}

function handleResetSelectedDate(state) {
  return { ...state, selectedDate: null }
}

function handleSetSelectedDate(state, { selectedDate }) {
  return { ...state, selectedDate }
}

function handleSetSnapshot(state, { snapshot }) {
  return { ...state, snapshot }
}

function handleLoadSnapshot(state, { payload }) {
  return { ...state, selectedModel: payload.modelId, snapshot: null }
}

export default createReducer(DEFAULT_STATE, {
  [Types.RESEARCH_TOPIC_PREFERENCES_SUCCESS]: handleResearchTopicPreferencesSuccess,
  [Types.SET_MODEL]: handleSetModel,
  [Types.SET_PERIOD]: handleSetPeriod,
  [Types.RESET_SELECTED_DATE]: handleResetSelectedDate,
  [Types.SET_SELECTED_DATE]: handleSetSelectedDate,
  [Types.LOAD_SNAPSHOT]: handleLoadSnapshot,
  [Types.SET_SNAPSHOT]: handleSetSnapshot,
})

function* handleLoadSuccess({ resource, data }) {
  if (resource === "researchTopicRootPreferences") {
    const { accountId, selectedModel, models, snapshot } = yield select(state => ({
      accountId: state.global.selected.accountId,
      selectedModel: state.modules.researchTopicRoot.selectedModel,
      models: getModels(state),
      snapshot: state.modules.researchTopicRoot.snapshot,
    }))
    if (snapshot) {
      yield put(Creators.loadSnapshot(snapshot))
    } else {
      let modelId = selectedModel
      if (!modelId) {
        modelId = getUserPreference(data, "researchTopicLastModelUsed", null)
      }

      // In case the user does not have access to this model anymore.
      if (modelId && findIndex(models, { id: modelId }) === -1) {
        modelId = null
        yield put(PreferencesCreators.deletePreferences([{ type: "researchTopicLastModelUsed", accountId }]))
      }

      yield put(Creators.researchTopicPreferencesSuccess(modelId))
    }
  } else if (resource === "topic") {
    yield put(Creators.resetSelectedDate)
  }
}

function* startUpdateTopic({ topicId, update }) {
  if (topicId && update) {
    const { account } = yield select(state => ({
      account: state.global.selected.account,
    }))

    const topic = {
      name: update.name,
      description: update.description,
    }

    const mutation = `mutation ($topicId: String!, $update: UpdateResearchTopic!) {
      updateResearchTopic (researchTopicId: $topicId, researchTopic: $update) {
        id,
        name,
        description,
        referenceInstrument {
          type,
          securityId { ticker mic country internalId }
        },
        lastUpdated
      }
    }`

    try {
      const { data } = yield call(sendQuery, mutation, { topicId, update: topic }, account)
      const result = get(data, "updateResearchTopic")

      if (result) {
        yield put(modelCreators.resetCollections(["accountResearchTopics", "topic"]))
        yield put(notificationCreators.add("success", null, "Research topic successfully updated."))
      } else {
        yield put(notificationCreators.add("error", null, "An error happened while updating your research topic."))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unknown error occurred while updating your research topic."))
    }
  }
}

function* startDeleteTopic({ id }) {
  if (id) {
    try {
      const { account } = yield select(state => ({
        account: state.global.selected.account,
      }))

      const mutation = "mutation ($id: String!) { deleteResearchTopic (researchTopicId: $id) { ok } }"

      const { data } = yield call(sendQuery, mutation, { id }, account)
      const status = get(data, "deleteResearchTopic.ok")

      if (status) {
        yield put(modelCreators.resetCollections(["accountResearchTopics", "topic"]))
        yield put(notificationCreators.add("success", null, "Research topic successfully deleted."))
        const { navigate } = yield getContext("router")
        yield call(navigate, `/${account}/home/resources`)
      } else {
        yield put(notificationCreators.add("error", null, "An error happened while deleting your research topic."))
      }
    } catch {
      yield put(notificationCreators.add("error", null, "An unexpected error occurred while deleting your research topic."))
    }
  }
}

export function* saga() {
  yield takeLatest(LoaderTypes.LOAD_SUCCESS, handleLoadSuccess)
  yield takeLatest(Types.UPDATE_TOPIC, startUpdateTopic)
  yield takeLatest(Types.DELETE_TOPIC, startDeleteTopic)
}
